import { Card, Text, Group, Stack, Grid, Badge, Center } from "@mantine/core";
import { IconClock, IconCalendar } from "@tabler/icons-react";

interface HourlyActivity {
  hour: number;
  dayOfWeek: number;
  commits: number;
}

interface HourOfDayHeatmapProps {
  hourlyActivity: HourlyActivity[];
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export function HourOfDayHeatmap({ hourlyActivity }: HourOfDayHeatmapProps) {
  // Create a map for quick lookup
  const activityMap = new Map<string, number>();
  hourlyActivity.forEach((activity) => {
    const key = `${activity.dayOfWeek}-${activity.hour}`;
    activityMap.set(key, activity.commits);
  });

  // Find max for color scaling
  const maxCommits = Math.max(...hourlyActivity.map((a) => a.commits), 1);

  // Calculate statistics
  const totalCommits = hourlyActivity.reduce((sum, a) => sum + a.commits, 0);
  const avgCommitsPerHour = totalCommits / Math.max(hourlyActivity.length, 1);

  // Find most active hour and day
  const mostActiveEntry = hourlyActivity.reduce(
    (max, curr) => (curr.commits > max.commits ? curr : max),
    hourlyActivity[0] || { hour: 0, dayOfWeek: 0, commits: 0 }
  );

  // Count active hours (hours with at least 1 commit)
  const activeHours = hourlyActivity.filter((a) => a.commits > 0).length;

  // Get color based on commit count - using theme violet colors
  const getColor = (count: number) => {
    if (count === 0) return "rgba(139, 92, 246, 0.05)"; // Very light violet
    const intensity = count / maxCommits;
    if (intensity > 0.75) return "rgba(139, 92, 246, 0.9)"; // Very high
    if (intensity > 0.5) return "rgba(139, 92, 246, 0.7)"; // High
    if (intensity > 0.25) return "rgba(139, 92, 246, 0.4)"; // Medium
    return "rgba(139, 92, 246, 0.2)"; // Low
  };

  return (
    <Grid>
      <Grid.Col span={{ base: 12, lg: 9 }}>
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          style={{
            backgroundColor: "var(--card-background)",
            border: "1px solid rgba(139, 92, 246, 0.4)",
          }}
        >
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <IconClock size={20} />
              <Text fw={600} size="lg">
                Commit Activity by Hour & Day
              </Text>
            </Group>
            <Badge variant="light" color="blue">
              Last 90 Days
            </Badge>
          </Group>

          <Stack gap="xs">
            {/* Header with hours */}
            <Group gap={4} wrap="nowrap">
              <div style={{ width: 40 }} /> {/* Space for day labels */}
              {HOURS.map((hour) => (
                <Center
                  key={hour}
                  style={{
                    width: 24,
                    fontSize: "10px",
                    color: "#868e96",
                  }}
                >
                  {hour % 3 === 0 ? hour : ""}
                </Center>
              ))}
            </Group>

            {/* Heatmap grid */}
            {DAYS.map((day, dayIndex) => (
              <Group key={day} gap={4} wrap="nowrap">
                <Text
                  size="xs"
                  style={{
                    width: 40,
                    textAlign: "right",
                    color: "#868e96",
                  }}
                >
                  {day}
                </Text>
                {HOURS.map((hour) => {
                  const key = `${dayIndex}-${hour}`;
                  const count = activityMap.get(key) || 0;
                  return (
                    <div
                      key={hour}
                      title={`${day} ${hour}:00 - ${count} commits`}
                      style={{
                        width: 24,
                        height: 24,
                        backgroundColor: getColor(count),
                        borderRadius: 3,
                        cursor: "pointer",
                        border: "1px solid #2c2e33",
                      }}
                    />
                  );
                })}
              </Group>
            ))}
          </Stack>

          <Text size="xs" c="dimmed" mt="md" ta="center">
            Hover over cells to see commit counts
          </Text>
        </Card>
      </Grid.Col>

      <Grid.Col span={{ base: 12, lg: 3 }}>
        <Stack gap="md">
          {/* Statistics Card */}
          <Card
            shadow="sm"
            padding="md"
            radius="md"
            style={{
              backgroundColor: "rgba(139, 92, 246, 0.05)",
              border: "1px solid rgba(139, 92, 246, 0.3)",
            }}
          >
            <Group gap="xs" mb="sm">
              <IconCalendar size={18} />
              <Text fw={600} size="sm">
                Activity Summary
              </Text>
            </Group>
            <Stack gap="xs">
              <div>
                <Text size="xs" c="dimmed">
                  Total Commits
                </Text>
                <Text fw={600} size="xl">
                  {totalCommits}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Avg per Hour
                </Text>
                <Text fw={600} size="lg">
                  {avgCommitsPerHour.toFixed(1)}
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Most Active
                </Text>
                <Text fw={600} size="sm">
                  {DAYS[mostActiveEntry.dayOfWeek]} {mostActiveEntry.hour}:00
                </Text>
                <Text size="xs" c="dimmed">
                  ({mostActiveEntry.commits} commits)
                </Text>
              </div>
              <div>
                <Text size="xs" c="dimmed">
                  Active Hours
                </Text>
                <Text fw={600} size="lg">
                  {activeHours}
                </Text>
              </div>
            </Stack>
          </Card>

          {/* Legend Card */}
          <Card
            shadow="sm"
            padding="md"
            radius="md"
            style={{
              backgroundColor: "rgba(139, 92, 246, 0.05)",
              border: "1px solid rgba(139, 92, 246, 0.3)",
            }}
          >
            <Text fw={600} size="sm" mb="sm">
              Intensity Legend
            </Text>
            <Stack gap="xs">
              <Group gap="xs">
                <div
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: "rgba(139, 92, 246, 0.05)",
                    borderRadius: 3,
                    border: "1px solid rgba(139, 92, 246, 0.3)",
                  }}
                />
                <Text size="xs">No activity</Text>
              </Group>
              <Group gap="xs">
                <div
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: "rgba(139, 92, 246, 0.2)",
                    borderRadius: 3,
                  }}
                />
                <Text size="xs">Low (1-25%)</Text>
              </Group>
              <Group gap="xs">
                <div
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: "rgba(139, 92, 246, 0.4)",
                    borderRadius: 3,
                  }}
                />
                <Text size="xs">Medium (26-50%)</Text>
              </Group>
              <Group gap="xs">
                <div
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: "rgba(139, 92, 246, 0.7)",
                    borderRadius: 3,
                  }}
                />
                <Text size="xs">High (51-75%)</Text>
              </Group>
              <Group gap="xs">
                <div
                  style={{
                    width: 16,
                    height: 16,
                    backgroundColor: "rgba(139, 92, 246, 0.9)",
                    borderRadius: 3,
                  }}
                />
                <Text size="xs">Very High (76-100%)</Text>
              </Group>
            </Stack>
          </Card>
        </Stack>
      </Grid.Col>
    </Grid>
  );
}
